import { useState } from 'react';
import { BLEND_MODE_GROUPS, BLEND_MODE_LABELS } from '../../core/blendModes';
import { hexToRgba, rgbaToHex } from '../../core/color';
import { allPatterns, getPattern } from '../../core/patterns';
import type { BlendMode, RGBA } from '../../core/types';
import { applyFill } from '../../state/actions';
import { fillWithPattern } from '../../state/patternActions';
import { useAppStore } from '../../state/store';
import { Modal, Select, SliderRow } from '../components/controls';
import { FILL_CONTENTS, type FillChoice } from './fillContents';

const BLEND_OPTIONS: { value: BlendMode; label: string }[] = BLEND_MODE_GROUPS.flat().map(
  (mode) => ({ value: mode, label: BLEND_MODE_LABELS[mode] }),
);

export function FillDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const foreground = useAppStore((state) => state.foreground);
  const [contents, setContents] = useState<FillChoice>('foreground');
  const [opacity, setOpacity] = useState(100);
  const [blendMode, setBlendMode] = useState<BlendMode>('normal');
  const [customColor, setCustomColor] = useState<RGBA>(foreground);
  const patterns = allPatterns();
  const activePatternId = useAppStore((state) => state.activePatternId);
  const setActivePattern = useAppStore((state) => state.setActivePattern);
  const patternId = activePatternId ?? patterns[0]?.id ?? null;

  const apply = () => {
    if (contents === 'pattern') {
      const pattern = getPattern(patternId);
      if (pattern) {
        fillWithPattern(pattern, { opacity: opacity / 100, blendMode });
      }
    } else if (contents === 'color') {
      applyFill('color', opacity / 100, { blendMode, color: customColor });
    } else {
      applyFill(contents, opacity / 100, { blendMode });
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Fill"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            OK
          </button>
        </>
      }
    >
      <p className="dialog__note">
        Fills the selection, or the whole layer when nothing is selected.
      </p>

      <div className="panel__section-title">Contents</div>
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Use
        </span>
        <Select value={contents} onChange={setContents} options={FILL_CONTENTS} />
      </div>
      {contents === 'color' && (
        <div className="field">
          <span className="field__label" style={{ width: 66 }}>
            Color
          </span>
          <label className="layer-style__color-swatch" style={{ background: rgbaToHex(customColor) }}>
            <input
              type="color"
              value={rgbaToHex(customColor)}
              onChange={(event) => setCustomColor(hexToRgba(event.target.value, customColor.a))}
              aria-label="Fill color"
            />
          </label>
        </div>
      )}
      {contents === 'pattern' && (
        <div className="field">
          <span className="field__label" style={{ width: 66 }}>
            Pattern
          </span>
          <Select
            value={patternId ?? ''}
            onChange={setActivePattern}
            options={patterns.map((pattern) => ({
              value: pattern.id,
              label: `${pattern.name} (${pattern.width}×${pattern.height})`,
            }))}
          />
        </div>
      )}

      <div className="panel__section-title" style={{ marginTop: 12 }}>
        Blending
      </div>
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Mode
        </span>
        <Select value={blendMode} onChange={setBlendMode} options={BLEND_OPTIONS} />
      </div>
      <SliderRow label="Opacity" value={opacity} min={0} max={100} onChange={setOpacity} />
    </Modal>
  );
}
